/**
 * Compact `Popularity` / `Stock` cells for inventory + replenishment views.
 *
 * Popularity code is the 2nd char of INITINFC's GICHAR1 (Rexel "FO1"):
 * A (most velocity) … G (slow), N / '' (non-stock). It is a **loose string** —
 * `im_inventory_policies.popularity_code` is `STRING(10)` with no enum, no check
 * constraint, and no shared-types vocabulary; whatever AS400 sends lands here.
 * So it renders as plain text, NOT a coloured chip: a colour ramp would imply a
 * validated, ordered vocabulary we don't have (and three different ramps had
 * already drifted apart across the procurement views). Give it real semantics
 * — a documented enum + one shared mapping — before styling it again.
 *
 * Stock status maps from GISTKFLG (`stocked` | `non_stock` | `special_order`) —
 * that IS a closed set, so it keeps its badge.
 * Both come from `im_inventory_policies` per product × warehouse.
 *
 * Renders `—` when null so the column doesn't collapse and keeps layout
 * predictable.
 */

import { T, useGT } from 'gt-react';

export function PopularityCode({ code }: { code: string | null | undefined }) {
  const gt = useGT();
  if (code == null || code.trim() === '') {
    return (
      <span
        className="text-xs text-gray-400"
        title={gt(
          'No popularity policy for this SKU × warehouse (treated as non-stock)',
          { context: 'popularity code null tooltip' },
        )}
      >
        —
      </span>
    );
  }
  return (
    <span
      className="text-xs font-medium tabular-nums text-gray-700"
      title={gt(
        'Pop from INITINFC FO1 (A=highest velocity, G=slow, N=non-stock)',
        { context: 'popularity code tooltip' },
      )}
    >
      {code.trim().toUpperCase()}
    </span>
  );
}

const STOCK_STATUS_TONE: Record<string, string> = {
  stocked: 'bg-emerald-100 text-emerald-700',
  non_stock: 'bg-gray-200 text-gray-600',
  special_order: 'bg-violet-100 text-violet-700',
};

function StockStatusLabel({ status }: { status: string }) {
  switch (status) {
    case 'stocked':
      return <T context="stock status label stocked">Stocked</T>;
    case 'non_stock':
      return <T context="stock status label non-stock">Non-stock</T>;
    case 'special_order':
      return <T context="stock status label special order">Special order</T>;
    default:
      return <>{status}</>;
  }
}

export function StockStatusBadge({
  status,
}: {
  status: string | null | undefined;
}) {
  const gt = useGT();
  if (status == null || status.trim() === '') {
    return <span className="text-xs text-gray-400">—</span>;
  }
  const key = status.trim().toLowerCase();
  const tone = STOCK_STATUS_TONE[key] ?? 'bg-gray-100 text-gray-700';
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}
      title={gt('Stock status from INITINFC GISTKFLG', {
        context: 'stock status badge tooltip',
      })}
    >
      <StockStatusLabel status={key} />
    </span>
  );
}
